import React, { useEffect, useState } from 'react'
import { Alert, Button, Nav, Spinner } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  fetchSreEvents,
  deleteSreEvent,
  setApplication,
  setDateRange,
  setStatusFilter,
  setSort,
  resetFilters,
} from '../../../reducers/sreSlice'
import SreFilters from '../../../components/SreFilters'
import SreTable from '../../../components/SreTable'
import SreDeleteModal from '../../../components/SreDeleteModal'

function sortEvents(events, sortColumn, sortDirection) {
  return [...events].sort((a, b) => {
    const aVal = a[sortColumn]
    const bVal = b[sortColumn]
    const dir = sortDirection === 'asc' ? 1 : -1
    if (aVal < bVal) return -dir
    if (aVal > bVal) return dir
    return 0
  })
}

function filterEvents(events, statusFilter) {
  if (statusFilter === 'success') return events.filter((e) => e.success)
  if (statusFilter === 'failure') return events.filter((e) => !e.success)
  return events
}

function AdminSre() {
  const dispatch = useDispatch()
  const {
    application,
    events,
    hasMore,
    loading,
    error,
    deleteError,
    statusFilter,
    sortColumn,
    sortDirection,
  } = useSelector((state) => state.sre)

  const [localStart, setLocalStart] = useState('')
  const [localEnd, setLocalEnd] = useState('')
  const [modalEvent, setModalEvent] = useState(null)
  const [selectedTimestamps, setSelectedTimestamps] = useState(new Set())
  const [bulkDeletePending, setBulkDeletePending] = useState(false)

  useEffect(() => {
    dispatch(fetchSreEvents({ application, start: null, end: null }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleApplicationChange(app) {
    dispatch(setApplication(app))
    setSelectedTimestamps(new Set())
    dispatch(fetchSreEvents({ application: app, start: null, end: null }))
  }

  function handleDateChange(field, value) {
    if (field === 'start') setLocalStart(value)
    else setLocalEnd(value)
  }

  function handleRefresh() {
    const s = localStart || null
    const e = localEnd || null
    setSelectedTimestamps(new Set())
    dispatch(setDateRange({ start: s, end: e }))
    dispatch(fetchSreEvents({ application, start: s, end: e }))
  }

  function handleReset() {
    setLocalStart('')
    setLocalEnd('')
    setSelectedTimestamps(new Set())
    dispatch(resetFilters())
    dispatch(fetchSreEvents({ application: 'stock-price-lambda', start: null, end: null }))
  }

  function handleToggleSelect(timestamp) {
    setSelectedTimestamps((prev) => {
      const next = new Set(prev)
      if (next.has(timestamp)) next.delete(timestamp)
      else next.add(timestamp)
      return next
    })
  }

  function handleToggleSelectAll() {
    const allSelected =
      displayedEvents.length > 0 &&
      displayedEvents.every((e) => selectedTimestamps.has(e.sre_timestamp))
    if (allSelected) {
      setSelectedTimestamps(new Set())
    } else {
      setSelectedTimestamps(new Set(displayedEvents.map((e) => e.sre_timestamp)))
    }
  }

  async function handleConfirmBulkDelete() {
    for (const sreTimestamp of [...selectedTimestamps]) {
      try {
        await dispatch(deleteSreEvent({ application, sreTimestamp })).unwrap()
      } catch {
        return
      }
    }
    setSelectedTimestamps(new Set())
    setBulkDeletePending(false)
  }

  async function handleConfirmDelete() {
    try {
      await dispatch(
        deleteSreEvent({ application, sreTimestamp: modalEvent.sre_timestamp })
      ).unwrap()
      setModalEvent(null)
    } catch {
      // deleteError set in slice
    }
  }

  const displayedEvents = sortEvents(
    filterEvents(events, statusFilter),
    sortColumn,
    sortDirection
  )

  return (
    <section className="py-4">
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/charities">Charity Manager</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/sre">SRE Events</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/stock">Stock Pricing</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/snp500">S&amp;P 500 List</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link as={NavLink} to="/admin/investments">Investments</Nav.Link>
        </Nav.Item>
      </Nav>

      <SreFilters
        application={application}
        start={localStart}
        end={localEnd}
        statusFilter={statusFilter}
        onApplicationChange={handleApplicationChange}
        onDateChange={handleDateChange}
        onStatusFilterChange={(f) => dispatch(setStatusFilter(f))}
        onRefresh={handleRefresh}
        onReset={handleReset}
      />

      {loading && (
        <div className="my-3">
          <Spinner animation="border" size="sm" role="status">
            <span className="visually-hidden">Loading…</span>
          </Spinner>
        </div>
      )}

      {error && !loading && (
        <Alert variant="danger" role="alert">
          {error}{' '}
          <Alert.Link
            as="button"
            onClick={() => dispatch(fetchSreEvents({ application, start: null, end: null }))}
          >
            Retry
          </Alert.Link>
        </Alert>
      )}

      {!error && (
        <>
          {selectedTimestamps.size > 0 && (
            <div className="mb-2">
              <Button variant="danger" size="sm" onClick={() => setBulkDeletePending(true)}>
                Delete Selected ({selectedTimestamps.size})
              </Button>
            </div>
          )}
          <SreTable
            events={displayedEvents}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={(col) => dispatch(setSort(col))}
            onDelete={setModalEvent}
            hasMore={hasMore}
            selectedTimestamps={selectedTimestamps}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
          />
        </>
      )}

      {modalEvent && (
        <SreDeleteModal
          event={modalEvent}
          onConfirm={handleConfirmDelete}
          onCancel={() => setModalEvent(null)}
          error={deleteError}
        />
      )}

      {bulkDeletePending && (
        <SreDeleteModal
          bulkCount={selectedTimestamps.size}
          onConfirm={handleConfirmBulkDelete}
          onCancel={() => setBulkDeletePending(false)}
          error={deleteError}
        />
      )}
    </section>
  )
}

export default AdminSre
